import {PdsWidget} from './pds-widget';
import {PoppableElementFactory} from './poppable-element-factory';
import {PopoverAccessibilityControllerFactory} from './popover-accessibility-controller-factory';
import {PopoverAccessibilityControllerIf} from './interfaces/popover-accessibility-controller-if';
import {pdsI18n} from './pds-i18n';
import {pdsUtils} from './pds-utils';
import {AppSwitcherItem} from './interfaces/app-switcher-item';
import {BehaviorSubject, Subscription} from 'rxjs';
import { pdsLogger } from './pds-logger';
import has = Reflect.has;
import {AppSwitcherItemRI} from './interfaces/app-switcher-item-ri';
import {SinglePopoverWidget} from './single-popover-widget';
import {PopperPlacement} from './popper-configuration';
import { versionAndPaths } from './version-and-paths';

let log = pdsLogger.prependLogger(pdsLogger.requiresLogger('app-switcher'), 'pds-app-switcher.ts');

export type AppSwitcherMessageEvent = MessageEvent & { data: {from: string, task: string, apps?: Array<AppSwitcherItemRI>} };
/**
 * We only ever want to add an event listener ONCE because we don't have a hook for when the element is destroyed.
 * Defining the listener here and keeping track of its existence ensures that we won't ever add more than one.
 * This listener also takes into account that there may be more than one app switcher in the DOM.
 * @type {boolean}
 */
let hasAddedMessageListener = false;
function addMessageListener(): void {
  if (!hasAddedMessageListener) {
    //ONLY ADD THE LISTENER ONCE
    window.addEventListener('message', function(event: AppSwitcherMessageEvent): void {
      let appSwitchers: NodeList = document.querySelectorAll('pds-app-switcher');
      for (let i = 0; i < appSwitchers.length; i++) {
        (<PdsAppSwitcher>(<any>appSwitchers[i])).handleMessageEvent(event);
      }
    });

    hasAddedMessageListener = true;
  }
}

export class PdsAppSwitcher extends SinglePopoverWidget {

  is = 'pds-app-switcher';

  scopeClass: string;

  // state: 'OPEN' | 'CLOSED';

  apps: Array<AppSwitcherItem> | null;

//The url for downloading the fetch-app-switcher-data.html file.
  iframeSrc: string | undefined;

  //determines which server to populate iframeSrc with and also
  //dictates which server the apps will be downloaded from.
  iframeSwitch: string | undefined | null;

  dataBackgroundColor: string | null;
  dataIconColor: string | null;
  dataIconColorHover: string | null;

  properties = {
    scopeClass: {
      type: String,
      value: ''
    },
    dataHasIcon: {
      type: String,
      value: 'true'
    },
    apps: {
      value: null,
      observer: 'updateVisibility'
    },
    motuGuid: {
      type: String,
      value: null,
      observer: 'motuGuidChanged'
    },
    dataBackgroundColor: {
      type: String,
      value: null,
      observer: 'mainButtonBackgroundColorChanged'
    },
    dataIconColor: {
      type: String,
      value: null,
      observer: 'appSwitcherIconColorChanged'
    },
    dataIconColorHover: {
      type: String,
      value: null,
      observer: 'appSwitcherIconColorHoverChanged'
    }
  };

  // HUGE TODO: This code is shared between this polymer component and the popover class in angular.
  // When we move the PDS popover over from Angular -> Polymer this will need to go to a shared location
  // leaving the visibility as protected

  protected popoverAccessibilityController: PopoverAccessibilityControllerIf;
  protected popoverTrigger: HTMLElement;
  protected popoverContentElement: HTMLElement;

  /**
   * The behavior subject stars out with a null value, and then gets updated whenever the motuGuid attribute/property changes.
   */
  private motuGuidObservable: BehaviorSubject<string | null>;

  /**
   * We only want to subscribe the the motuGidObservable once, so we use this variable to remember if we have subscribed or not.
   */
  private motuGuidSubscription: Subscription | undefined;

  isRequiredDataDefined(): boolean {
    return this.apps != null;
  }

  getPopoverSelector(): string {
    return '#app-switcher-popover';
  }

  getPopoverTriggerSelector(): string {
    return '.pds-app-switcher-trigger';
  }

  //Polymer hook - called immediately after the constructor was called.
  //NOTE: member variables set here can not be directly accessed in the template.
  created(): void {
    log('app switcher created');
    this.motuGuidObservable = new BehaviorSubject<string | null>(null);
    this.iframeSwitch = localStorage.getItem('pds-app-switcher-iframe-switch');
    switch (this.iframeSwitch) {
      case 'attach-apps-manually':
        //no-op.
        break;
      case 'pds-site':
        this.iframeSrc = 'app-switcher-support-files/fetch-app-switcher-data.html';
        break;
      case 'local':
        this.iframeSrc = 'http://localhost:9090/local-cdn/html/fetch-app-switcher-data.html';
        break;
      case 'test':
        this.iframeSrc = `https://assets.powerschool.com/pds/${versionAndPaths.version}/html/fetch-app-switcher-data.html`;
        break;
      case 'stage':
        this.iframeSrc = `https://assets.powerschool.com/pds/${versionAndPaths.version}/html/fetch-app-switcher-data.html`;
        break;
      case 'production-ca':
        this.iframeSrc = `https://assets.powerschool.com/pds/${versionAndPaths.version}/html/fetch-app-switcher-data.html`;
        break;
      case 'production':
      default:
        this.iframeSrc = `https://assets.powerschool.com/pds/${versionAndPaths.version}/html/fetch-app-switcher-data.html`;
        break;
    }

    addMessageListener();
  }

  //Polymer hook - Called after the property values have been set
  ready(): void {
    super.ready();
    if (this.iframeSrc != null) {
      this.attachIFrame();
    }
    this.style.display = 'none';
  }

  // polymer hook - Called each time the element is attached to the DOM.
  attached(): void {
    log('app switcher attached');
    super.attached();
  }

  motuGuidChanged(motuGuid: string | null): void {
    log('moduGuid changed to: ' + motuGuid);
    this.motuGuidObservable.next(motuGuid);
  }

  // App Switcher Button & Icon Custom Color Styles //
  mainButtonBackgroundColorChanged(): void {
    pdsUtils.remove(this.querySelector('#pds-main-button-background-color'));
    if (this.dataBackgroundColor == null) {
      return;
    }

    let styleTag = document.createElement('style');
    styleTag.innerHTML =
      `
      #pds-app-switcher {
        background-color: ${this.dataBackgroundColor};
      }`;
    styleTag.setAttribute('id', 'pds-main-button-background-color');
    this.appendChild(styleTag);
  }

  appSwitcherIconColorChanged(): void {
    pdsUtils.remove(this.querySelector('#pds-app-switcher-icon-color'));
    if (this.dataIconColor == null) {
      return;
    }

    let styleTag = document.createElement('style');
    styleTag.innerHTML =
      `
      #pds-app-switcher pds-icon svg {
        fill: ${this.dataIconColor};
      }`;
    styleTag.setAttribute('id', 'pds-app-switcher-icon-color');
    this.appendChild(styleTag);
  }

  appSwitcherIconColorHoverChanged(): void {
    pdsUtils.remove(this.querySelector('#pds-app-switcher-icon-color-hover'));
    if (this.dataIconColorHover == null) {
      return;
    }

    let styleTag = document.createElement('style');
    styleTag.innerHTML =
      `
      #pds-app-switcher:hover pds-icon,
      #pds-app-switcher:focus pds-icon {
        background-color: ${this.dataIconColorHover};
        border-radius: 50%;
      }`;
    styleTag.setAttribute('id', 'pds-app-switcher-icon-color-hover');
    this.appendChild(styleTag);
  }
  // END App Switcher Button & Icon Custom Color Styles //

  attachIFrame(): void {
    if (this.iframeSrc == null) {
      log('not attaching iFrame');
      return;
    }

    log('attaching the iFrame');

    //Removes the existing iframe if it exists
    pdsUtils.remove(this.querySelector('iframe'));

    let myIFrame = document.createElement('iframe');

    myIFrame.setAttribute('src', this.iframeSrc);
    myIFrame.style.display = 'none';
    this.querySelector('.pds-app-switcher-contents').appendChild(myIFrame);
  }

  /**
   * This is called by the message listener added to the window.
   */
  handleMessageEvent(event: AppSwitcherMessageEvent): void {
    log('PdsAppSwitcher.handleMessageEvent');
    log(event);

    let myIFrame = this.querySelector('iframe');

    if (event.data != null && event.data.from === 'fetch-app-switcher-data') {
      switch (event.data.task) {
        case 'request-inputs':
          //We only ever want ONE subscription.
          if (this.motuGuidSubscription == null) {
            //No subscription yet. Let's create one.
            this.motuGuidSubscription = this.motuGuidObservable.subscribe((motuGuid: string | null) => {
              if (motuGuid == null) {
                return;
              }

              (<any>myIFrame).contentWindow.postMessage({
                from: 'pds-app-switcher',
                task: 'fetch-data',
                iframeSwitch: this.iframeSwitch,
                motuGuid,
                loggerStatus: pdsLogger.loggerStatus['app-switcher']
              }, '*');
            });
          } else {
            //We already have a subscription. Let's just fire the observable so the subscription will run.
            this.motuGuidObservable.next(this.motuGuidObservable.value);
          }
          break;
        case 'assign-apps':
          log('assigning apps');
          this.apps = this.convertApps(event.data.apps);
          break;
        default:
          console.error('Received a task: "' + event.data.task + '" from the iframe that is not not supported.');
      }
    }
  }

  createPopoverPanel(): void {
    super.createPopoverPanel();
    this.updateVisibility();
  }

  /**
   * Updates the visibility of the app switcher button in the UI based on whether enough apps are present or not.
   */
  updateVisibility(): void {
    this.style.display = this.apps == null || this.apps.length < 2 || this.popoverInstance == null ? 'none' : '';
    log('visibility set to: "' + this.style.display + '"');
  }

  /**
   * Converts a list of AppSwithcerItemRIs to a list of AppSwitcherItems
   * @param appRIs The list to convert.
   * @returns {Array<AppSwitcherItem>} The converted list.
   */
  convertApps(appRIs: Array<AppSwitcherItemRI> | undefined): Array<AppSwitcherItem> {
    log('converting apps');
    let apps: Array<AppSwitcherItem> = [];
    if (appRIs != null) {
      for (let appRI of appRIs) {
        apps.push({
          id: appRI.ProductId,
          name: appRI.ProductName,
          initials: appRI.initials,
          url: appRI.ProductUrl,
          colorCategoryNumber: appRI.colorCategoryNumber,
          productIcon: appRI.ProductIcon || undefined //Makes empty strings
        });
      }
    }
    return apps;
  }

  log(message?: any, ...optionalParams: any[]): void {
    log.apply(window, arguments);
  }

  getDefaultAlignment(): PopperPlacement {
    return 'bottom-end';
  }
}
